

export const frontMatter = {
	sidebar_position: 2,
	license: 'republish',
	author: 'Jan van Steenbergen',
	origin: 'http://steen.free.fr/interslavic/adjectives.html',
	description: 'Explore the inflection, short form, adverbs, and degrees of comparison of adjectives in Interslavic. Learn about regular and irregular forms, comparative and superlative structures, and usage examples.',
	tags: [
		'interslavic',
		'adjectives',
		'inflection',
		'degrees of comparison',
		'language learning'
	]
};
export const contentTitle = 'Adjectives';
export {default as metadata} from '@site/.docusaurus/docusaurus-plugin-content-docs/default/site-docs-learn-grammar-adjectives-md-c6f.json'
export const assets = {

};

import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
export const toc = [{
  "value": "Inflection",
  "id": "inflection",
  "level": 2
}, {
  "value": "Basic endings",
  "id": "basic-endings",
  "level": 3
}, {
  "value": "Hard adjectives",
  "id": "hard-adjectives",
  "level": 3
}, {
  "value": "Soft adjectives",
  "id": "soft-adjectives",
  "level": 3
}, {
  "value": "Short form",
  "id": "short-form",
  "level": 2
}, {
  "value": "Adverbs",
  "id": "adverbs",
  "level": 2
}, {
  "value": "Degrees of comparison",
  "id": "degrees-of-comparison",
  "level": 2
}, {
  "value": "Analytic comparative",
  "id": "analytic-comparative",
  "level": 3
}, {
  "value": "Synthetic comparative",
  "id": "synthetic-comparative",
  "level": 3
}, {
  "value": "Irregular comparatives",
  "id": "irregular-comparatives",
  "level": 3
}, {
  "value": "Superlative",
  "id": "superlative",
  "level": 3
}, {
  "value": "Additional forms",
  "id": "additional-forms",
  "level": 3
}, {
  "value": "Example",
  "id": "example",
  "level": 3
}];
function _createMdxContent(props) {
  const _components = {
    a: "a",
    abbr: "abbr",
    code: "code",
    h1: "h1",
    h2: "h2",
    h3: "h3",
    header: "header",
    li: "li",
    ol: "ol",
    p: "p",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  }, {CustomAfterSection, CustomBeforeSection, MDAdjectives1, MDAdjectives2, MDAdjectives3, TransliteratorElement} = _components;
  if (!CustomAfterSection) _missingMdxReference("CustomAfterSection", true);
  if (!CustomBeforeSection) _missingMdxReference("CustomBeforeSection", true);
  if (!MDAdjectives1) _missingMdxReference("MDAdjectives1", true);
  if (!MDAdjectives2) _missingMdxReference("MDAdjectives2", true);
  if (!MDAdjectives3) _missingMdxReference("MDAdjectives3", true);
  if (!TransliteratorElement) _missingMdxReference("TransliteratorElement", true);
  return _jsxs(_Fragment, {
    children: [_jsx(_components.header, {
      children: _jsx(_components.h1, {
        id: "adjectives",
        children: "Adjectives"
      })
    }), "\n", _jsx(_components.h2, {
      id: "inflection",
      children: "Inflection"
    }), "\n", _jsx(CustomBeforeSection, {}), "\n", _jsx(_components.p, {
      children: "Adjectives match with the noun they modify in gender, case and number. Declension is always regular. However, three things are to be remembered:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["Adjectives are heavily affected by the ", _jsxs(_components.a, {
          href: "/learn/phonology#o--e",
          children: [_jsx(TransliteratorElement, {
            children: _jsx(_components.code, {
              children: "o"
            })
          }), "/", _jsx(TransliteratorElement, {
            children: _jsx(_components.code, {
              children: "e"
            })
          })]
        }), " and ", _jsxs(_components.a, {
          href: "/learn/phonology#y--ie",
          children: [_jsx(TransliteratorElement, {
            children: _jsx(_components.code, {
              children: "y"
            })
          }), "/", _jsx(TransliteratorElement, {
            children: _jsx(_components.code, {
              children: "i"
            })
          })]
        }), " rules, which means that a distinction is made between hard stems and soft stems: if the stem ends in ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "š"
          })
        }), ", ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "ž"
          })
        }), ", ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "č"
          })
        }), " or ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "j"
          })
        }), ", then every ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-o-"
          })
        }), " becomes ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-e-"
          })
        }), " and every ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-y-"
          })
        }), " becomes ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-i-"
          })
        }), "."]
      }), "\n", _jsx(_components.li, {
        children: "Just like in the case of nouns, the accusative of has the same form as the genitive when the corresponding noun is masculine and animate, both singular and plural."
      }), "\n", _jsx(_components.li, {
        children: "Except for adjectives referring to masculine, animate nouns, the nominate and accusative plural are identical for all genders."
      }), "\n"]
    }), "\n", _jsx(_components.h3, {
      id: "basic-endings",
      children: "Basic endings"
    }), "\n", _jsx(_components.p, {
      children: "The basic endings (applying not only to adjectives, but also to possessive pronouns, demonstrative pronouns and the like) are:"
    }), "\n", _jsx(MDAdjectives1, {}), "\n", _jsx(_components.h3, {
      id: "hard-adjectives",
      children: "Hard adjectives"
    }), "\n", _jsxs(_components.p, {
      children: ["Example of the declension of hard adjectives: ", _jsx(TransliteratorElement, {
        children: "dobry"
      }), " „good”:"]
    }), "\n", _jsx(MDAdjectives2, {}), "\n", _jsx(_components.h3, {
      id: "soft-adjectives",
      children: "Soft adjectives"
    }), "\n", _jsxs(_components.p, {
      children: ["Example of the declension of soft adjectives: ", _jsx(TransliteratorElement, {
        children: "svěži"
      }), " „fresh”:"]
    }), "\n", _jsx(MDAdjectives3, {}), "\n", _jsx(_components.h2, {
      id: "short-form",
      children: "Short form"
    }), "\n", _jsxs(_components.p, {
      children: ["A small number of adjectives exist in the so-called short form, a relic of the indefinite declension of adjectives in Common Slavic. This short form differs from regular adjectives only in the masculine nominative singular by omitting the ending ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-y"
        })
      }), "/", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-i"
        })
      }), ". The most common examples are the possessive adjectives ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-ov"
        })
      }), " and ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-in"
        })
      }), ", respecively expressing ownership by a masculine and feminine person: ", _jsx(TransliteratorElement, {
        children: "Petrov dom"
      }), " „Peter’s house”, ", _jsx(TransliteratorElement, {
        children: "materin stol"
      }), " „mother’s table”. All other forms are regular: ", _jsx(TransliteratorElement, {
        children: "Petrova kniga"
      }), " „Peter’s book”, ", _jsx(TransliteratorElement, {
        children: "na materinom stolu"
      }), " „on mother’s table”."]
    }), "\n", _jsxs(_components.p, {
      children: ["These possessive adjectives are only used when the possessor is a single word. In other cases, the genitive is used: ", _jsx(TransliteratorElement, {
        children: "bratova kniga"
      }), " „brother’s book”, but: ", _jsx(TransliteratorElement, {
        children: "kniga mojego brata"
      }), " „my brother’s book”; ", _jsx(TransliteratorElement, {
        children: "Puškinova poezija"
      }), " „Pushkin’s poetry”, but: ", _jsx(TransliteratorElement, {
        children: "poezija Aleksandra Puškina"
      }), " „Aleksandr Pushkin’s poetry”."]
    }), "\n", _jsxs(_components.p, {
      children: ["The short form may also be used when the adjective forms the predicate of the sentence: ", _jsx(TransliteratorElement, {
        children: "Petr jest ščestliv"
      }), " „Peter is happy”, ", _jsx(TransliteratorElement, {
        children: "dom jest velik"
      }), " „the house is big”, etc. This is not mandatory though, and in most cases, people will simply write: ", _jsx(TransliteratorElement, {
        children: "Petr jest ščestlivy"
      }), " and ", _jsx(TransliteratorElement, {
        children: "dom jest veliky"
      }), "."]
    }), "\n", _jsx(_components.h2, {
      id: "adverbs",
      children: "Adverbs"
    }), "\n", _jsxs(_components.p, {
      children: ["Adverbs can be derived from adjectives by using the ending ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-o"
        })
      }), " (", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-e"
        })
      }), " after a soft consonant). In other words, they are identical to the neuter singular: ", _jsx(TransliteratorElement, {
        children: "dobro"
      }), " „well”, ", _jsx(TransliteratorElement, {
        children: "svěže"
      }), " „freshly”."]
    }), "\n", _jsx(_components.h2, {
      id: "degrees-of-comparison",
      children: "Degrees of comparison"
    }), "\n", _jsx(_components.p, {
      children: "Comparatives and superlatives can be built in two ways: a simple (analytic) form and a more complex (synthetic) form."
    }), "\n", _jsx(_components.h3, {
      id: "analytic-comparative",
      children: "Analytic comparative"
    }), "\n", _jsxs(_components.p, {
      children: ["This way of forming a comparative is easiest to use. Simply have the ground form (the positive) of an adjective or adverb preceded by ", _jsx(TransliteratorElement, {
        children: "vyše"
      }), " or ", _jsx(TransliteratorElement, {
        children: "bolje"
      }), " „more”: ", _jsx(TransliteratorElement, {
        children: "vyše prosty"
      }), " „more simple = simpler”. This solution can be used for all adjectives and adverbs, but is most likely encountered in the case of very long words, foreign words, and participles: ", _jsx(TransliteratorElement, {
        children: "vyše/bolje sintetičny"
      }), " „more synthetic”, ", _jsx(TransliteratorElement, {
        children: "vyše/bolje smrdeči"
      }), " „more stinking”."]
    }), "\n", _jsx(_components.h3, {
      id: "synthetic-comparative",
      children: "Synthetic comparative"
    }), "\n", _jsxs(_components.p, {
      children: ["The basic comparative ending for adjectives ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-ějši"
        })
      }), " (", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-ejši"
        })
      }), " after a soft consonant), replacing the ending ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-y"
        })
      }), "/", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-i"
        })
      }), ": ", _jsx(TransliteratorElement, {
        children: "bogaty"
      }), " → ", _jsx(TransliteratorElement, {
        children: "bogatějši"
      }), ", ", _jsx(TransliteratorElement, {
        children: "slaby"
      }), " → ", _jsx(TransliteratorElement, {
        children: "slabějši"
      }), ", ", _jsx(TransliteratorElement, {
        children: "blagy"
      }), " → ", _jsx(TransliteratorElement, {
        children: "blažejši"
      }), ", ", _jsx(TransliteratorElement, {
        children: "svěži"
      }), " → ", _jsx(TransliteratorElement, {
        children: "svěžejši"
      }), "."]
    }), "\n", _jsxs(_components.p, {
      children: ["These endings can simply be made into adverbs by replacing the element ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-ši"
        })
      }), " by ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-e"
        })
      }), " (giving the ending ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-ěje"
        })
      }), "/", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-eje"
        })
      }), " instead of the adverbial ending ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-o"
        })
      }), "/", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-e"
        })
      }), "): ", _jsx(TransliteratorElement, {
        children: "novo"
      }), " → ", _jsx(TransliteratorElement, {
        children: "nověje"
      }), ", ", _jsx(TransliteratorElement, {
        children: "čisto"
      }), " → ", _jsx(TransliteratorElement, {
        children: "čistěje"
      }), ", ", _jsx(TransliteratorElement, {
        children: "tiho"
      }), " → ", _jsx(TransliteratorElement, {
        children: "tišeje"
      }), ", ", _jsx(TransliteratorElement, {
        children: "svěže"
      }), " → ", _jsx(TransliteratorElement, {
        children: "svěžeje"
      }), "."]
    }), "\n", _jsxs(_components.p, {
      children: ["An exception are adjectives on ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-ky"
        })
      }), ", ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-eky"
        })
      }), ", ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-oky"
        })
      }), ", which have ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-ši"
        })
      }), " instead: ", _jsx(TransliteratorElement, {
        children: "kratky"
      }), " → ", _jsx(TransliteratorElement, {
        children: "kratši"
      }), ", ", _jsx(TransliteratorElement, {
        children: "tenky"
      }), " → ", _jsx(TransliteratorElement, {
        children: "tenši"
      }), ", ", _jsx(TransliteratorElement, {
        children: "daleky"
      }), " → ", _jsx(TransliteratorElement, {
        children: "dalši"
      }), ", ", _jsx(TransliteratorElement, {
        children: "vysoky"
      }), " → ", _jsx(TransliteratorElement, {
        children: "vysši"
      }), "."]
    }), "\n", _jsxs(_components.p, {
      children: ["The comparatives of these adjectives can be adverbialised by adding the ending ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-je"
        })
      }), " to the root, causing ", _jsx(_components.a, {
        href: "/learn/phonology#iotation",
        children: "iotation"
      }), " of the preceding consonant(s): ", _jsx(TransliteratorElement, {
        children: "daleko"
      }), " → ", _jsx(TransliteratorElement, {
        children: "dalje"
      }), ", ", _jsx(TransliteratorElement, {
        children: "široko"
      }), " → ", _jsx(TransliteratorElement, {
        children: "širje"
      }), ", ", _jsx(TransliteratorElement, {
        children: "vysoky"
      }), " → ", _jsx(TransliteratorElement, {
        children: "vyše"
      }), ", ", _jsx(TransliteratorElement, {
        children: "blizko"
      }), " → ", _jsx(TransliteratorElement, {
        children: "bliže"
      }), ", ", _jsx(TransliteratorElement, {
        children: "rědko"
      }), " → ", _jsx(TransliteratorElement, {
        children: "rědže"
      }), "."]
    }), "\n", _jsx(_components.h3, {
      id: "irregular-comparatives",
      children: "Irregular comparatives"
    }), "\n", _jsx(_components.p, {
      children: "Seven adjectives have an irregular comparative:"
    }), "\n", _jsxs(_components.ol, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(TransliteratorElement, {
          children: "dobry"
        }), " „good” → ", _jsx(TransliteratorElement, {
          children: "lěpši"
        }), " (", _jsx(_components.abbr, {
          title: "adverb",
          children: "adv."
        }), " ", _jsx(TransliteratorElement, {
          children: "lěpje"
        }), ") or ", _jsx(TransliteratorElement, {
          children: "lučši"
        }), " (", _jsx(_components.abbr, {
          title: "adverb",
          children: "adv."
        }), " ", _jsx(TransliteratorElement, {
          children: "lučše"
        }), ") „better”"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(TransliteratorElement, {
          children: "zly"
        }), " „bad” → ", _jsx(TransliteratorElement, {
          children: "gorši"
        }), " (", _jsx(_components.abbr, {
          title: "adverb",
          children: "adv."
        }), " ", _jsx(TransliteratorElement, {
          children: "gorje"
        }), ") „worse”"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(TransliteratorElement, {
          children: "veliky"
        }), " „big, large” → ", _jsx(TransliteratorElement, {
          children: "večši"
        }), " (", _jsx(_components.abbr, {
          title: "adverb",
          children: "adv."
        }), " ", _jsx(TransliteratorElement, {
          children: "veče"
        }), ") or ", _jsx(TransliteratorElement, {
          children: "bolši"
        }), " (", _jsx(_components.abbr, {
          title: "adverb",
          children: "adv."
        }), " ", _jsx(TransliteratorElement, {
          children: "bolje"
        }), ") „bigger, larger”"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(TransliteratorElement, {
          children: "maly"
        }), " „little, small” → ", _jsx(TransliteratorElement, {
          children: "menši"
        }), " (", _jsx(_components.abbr, {
          title: "adverb",
          children: "adv."
        }), " ", _jsx(TransliteratorElement, {
          children: "menje"
        }), ") „smaller, less”"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(TransliteratorElement, {
          children: "blagy"
        }), " „pleasant, joyful” → ", _jsx(TransliteratorElement, {
          children: "unši"
        }), " (", _jsx(_components.abbr, {
          title: "adverb",
          children: "adv."
        }), " ", _jsx(TransliteratorElement, {
          children: "unje"
        }), ") „more pleasant/joyful”"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(TransliteratorElement, {
          children: "legky"
        }), " „easy, light” → ", _jsx(TransliteratorElement, {
          children: "legši"
        }), " (", _jsx(_components.abbr, {
          title: "adverb",
          children: "adv."
        }), " ", _jsx(TransliteratorElement, {
          children: "legše"
        }), ") „easier, lighter”"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(TransliteratorElement, {
          children: "mekky"
        }), " „soft” → ", _jsx(TransliteratorElement, {
          children: "mekši"
        }), " (", _jsx(_components.abbr, {
          title: "adverb",
          children: "adv."
        }), " ", _jsx(TransliteratorElement, {
          children: "mekše"
        }), ") „softer”"]
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["It is not impossible to use regular forms of these adjectives (like ", _jsx(TransliteratorElement, {
        children: "dobrějši"
      }), " or ", _jsx(TransliteratorElement, {
        children: "zlějši"
      }), "), but to the average Slav this will look childish or artificial."]
    }), "\n", _jsx(_components.h3, {
      id: "superlative",
      children: "Superlative"
    }), "\n", _jsxs(_components.p, {
      children: ["The superlative is formed by adding the prefix ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "naj-"
        })
      }), " to the comparative: ", _jsx(TransliteratorElement, {
        children: "najnovějši"
      }), " (", _jsx(_components.abbr, {
        title: "adjective",
        children: "adj."
      }), "), ", _jsx(TransliteratorElement, {
        children: "najnověje"
      }), " (", _jsx(_components.abbr, {
        title: "adverb",
        children: "adv."
      }), "). This works for the analytic comparative, too: ", _jsx(TransliteratorElement, {
        children: "najvyše sintetičny"
      }), " or ", _jsx(TransliteratorElement, {
        children: "najbolje sintetičny"
      }), "."]
    }), "\n", _jsxs(_components.p, {
      children: ["As a means of simplification, this prefix can also be added to the positive instead (which is how superlatives are made in Bulgarian and Macedonian): ", _jsx(TransliteratorElement, {
        children: "najnovy"
      }), " (", _jsx(_components.abbr, {
        title: "adjective",
        children: "adj."
      }), "), ", _jsx(TransliteratorElement, {
        children: "najnovo"
      }), " (", _jsx(_components.abbr, {
        title: "adverb",
        children: "adv."
      }), ")."]
    }), "\n", _jsx(_components.h3, {
      id: "additional-forms",
      children: "Additional forms"
    }), "\n", _jsxs(_components.p, {
      children: ["The adverbs ", _jsx(TransliteratorElement, {
        children: "menje"
      }), " and ", _jsx(TransliteratorElement, {
        children: "najmenje"
      }), " can be used to express the opposite meaning of ", _jsx(TransliteratorElement, {
        children: "vyše"
      }), "/", _jsx(TransliteratorElement, {
        children: "bolje"
      }), " and ", _jsx(TransliteratorElement, {
        children: "najvyše"
      }), "/", _jsx(TransliteratorElement, {
        children: "najbolje"
      }), ", meaning „less” and „least” respectively."]
    }), "\n", _jsxs(_components.p, {
      children: ["The prefix ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "prě-"
        })
      }), " attached to the positive can be used as a kind of superlative as well, expressing an extreme or excessive degree („very”, „too”)."]
    }), "\n", _jsxs(_components.p, {
      children: ["The prefix ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ne-"
        })
      }), " creates a negative: ", _jsx(TransliteratorElement, {
        children: "neprijetny"
      }), " „not pleasant, unpleasant”."]
    }), "\n", _jsx(_components.h3, {
      id: "example",
      children: "Example"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(TransliteratorElement, {
          children: "prosty"
        }), " „simple” (adjective, positive)"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(TransliteratorElement, {
          children: "prosto"
        }), " „simply” (adverb, positive)"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(TransliteratorElement, {
          children: "prostějši"
        }), " „simpler” or ", _jsx(TransliteratorElement, {
          children: "vyše prosty"
        }), " or ", _jsx(TransliteratorElement, {
          children: "bolje prosty"
        }), " „more simple, simpler”"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(TransliteratorElement, {
          children: "prostěje"
        }), " „simplier” or ", _jsx(TransliteratorElement, {
          children: "vyše prosto"
        }), " or ", _jsx(TransliteratorElement, {
          children: "bolje prosto"
        }), " „more simply, simplier”"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(TransliteratorElement, {
          children: "najprostějši"
        }), " or ", _jsx(TransliteratorElement, {
          children: "najprosty"
        }), " or ", _jsx(TransliteratorElement, {
          children: "najvyše prosty"
        }), " or ", _jsx(TransliteratorElement, {
          children: "najbolje prosty"
        }), " „simplest”"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(TransliteratorElement, {
          children: "najprostěje"
        }), " or ", _jsx(TransliteratorElement, {
          children: "najprosto"
        }), " or ", _jsx(TransliteratorElement, {
          children: "najvyše prosto"
        }), " or ", _jsx(TransliteratorElement, {
          children: "najbolje prosto"
        }), " „simpliest, most simply”"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(TransliteratorElement, {
          children: "menje prosty"
        }), " „less simple”"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(TransliteratorElement, {
          children: "menje prosto"
        }), " „less simply”"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(TransliteratorElement, {
          children: "najmenje prosty"
        }), " „least simple”"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(TransliteratorElement, {
          children: "najmenje prosto"
        }), " „least simply”"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(TransliteratorElement, {
          children: "neprosty"
        }), " „not simple”"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(TransliteratorElement, {
          children: "prěprosty"
        }), " „too simple, overly simple, extremely simple”"]
      }), "\n"]
    }), "\n", _jsx(CustomAfterSection, {})]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}

